import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import { ThemeToggle } from "@/components/ThemeToggle";
import { runBrowserHandoffs } from "@/utils/webusbPrinter";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  SquaresFour,
  CookingPot,
  ChartLine,
  Gear,
  SignOut,
  ForkKnife,
  User,
  UserList,
  AddressBook,
  CalendarBlank,
  Star,
  Buildings,
  Package,
  ChatText,
  ClockCounterClockwise,
  Timer,
  CaretDown,
  CaretRight,
  Briefcase,
  CurrencyDollar,
  Money,
  Monitor
} from "@phosphor-icons/react";

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [mgmtOpen, setMgmtOpen] = useState(true);
  // Company logo + name (falls back to ForkKnife + "Crest POS"). Cached
  // in sessionStorage so route changes don't blink while the /branding
  // roundtrip is in flight. Re-fetches whenever Settings dispatches the
  // ``crest:branding-updated`` window event so a re-login isn't required.
  const [logoUrl, setLogoUrl] = useState(() => sessionStorage.getItem("pos_company_logo") || "");
  const [brandName, setBrandName] = useState(
    () => sessionStorage.getItem("pos_company_name") || "Crest POS"
  );
  useEffect(() => {
    let cancelled = false;
    const load = () => axios.get(`${API}/branding`).then(({ data }) => {
      if (cancelled) return;
      const url = data?.logo_url || "";
      setLogoUrl(url);
      if (url) sessionStorage.setItem("pos_company_logo", url);
      else sessionStorage.removeItem("pos_company_logo");
      const name = (data?.website_title || data?.company_name || "").trim() || "Crest POS";
      setBrandName(name);
      sessionStorage.setItem("pos_company_name", name);
    }).catch(() => { /* fall back to icon + default name */ });
    load();
    window.addEventListener("crest:branding-updated", load);
    return () => {
      cancelled = true;
      window.removeEventListener("crest:branding-updated", load);
    };
  }, []);

  // Main nav items — Settings lives under Management now, so it's no
  // longer duplicated at the top level.
  const mainItems = [
    { icon: SquaresFour, label: "POS", path: "/pos", roles: ["admin", "manager", "server", "cashier"] },
    { icon: CalendarBlank, label: "Reservations", path: "/reservations", roles: ["admin", "manager", "server"] },
    { icon: CookingPot, label: "Kitchen", path: "/kitchen", roles: ["admin", "manager", "server", "cashier", "kds", "cook"] },
    { icon: ChatText, label: "Feedback", path: "/feedback-dashboard", roles: ["admin", "manager"] },
    { icon: Package, label: "Inventory", path: "/inventory", roles: ["admin", "manager"] },
    { icon: Buildings, label: "Locations", path: "/locations", roles: ["admin"] },
    { icon: AddressBook, label: "Customers", path: "/customers", roles: ["admin", "manager"] },
  ];

  // Management sub-items — POS Settings has been merged into
  // Settings → Equipment → POS, so the standalone entry is gone.
  const mgmtItems = [
    { icon: UserList, label: "Users", path: "/users", roles: ["admin", "manager"] },
    { icon: ChartLine, label: "Reports", path: "/reports", roles: ["admin", "manager"] },
    { icon: ClockCounterClockwise, label: "Shifts", path: "/shifts", roles: ["admin", "manager"] },
    { icon: Timer, label: "Time Clock", path: "/timeclock", roles: ["admin", "manager"] },
    { icon: CurrencyDollar, label: "Labor Costs", path: "/labor-costs", roles: ["admin", "manager"] },
    { icon: Gear, label: "Settings", path: "/settings", roles: ["admin", "manager"] },
  ];

  const filteredMain = mainItems.filter(item => item.roles.includes(user?.role));
  const filteredMgmt = mgmtItems.filter(item => item.roles.includes(user?.role));
  const showMgmt = filteredMgmt.length > 0;
  const isMgmtActive = mgmtItems.some(item => location.pathname === item.path);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleOpenCashDrawer = async () => {
    try {
      const res = await axios.post(`${API}/cash-drawer/open`, { reason: "manual" });
      let fired = res.data?.pulse_fired_count ?? 0;
      const total = res.data?.pulse_results?.length ?? 0;
      const handoffs = res.data?.browser_handoff_results ?? [];

      // Fire any WebUSB drawer-kicks from the browser side (backend can't
      // reach them). Each success bumps the pulse counter so the toast
      // reflects the true total.
      const handoffResult = await runBrowserHandoffs(handoffs);
      fired += handoffResult.fired;
      const webusbErrors = handoffResult.errors;

      if (fired > 0 && webusbErrors.length === 0) {
        toast.success(`Cash drawer pulsed (${fired}/${total} printer${total === 1 ? "" : "s"})`);
      } else if (fired > 0 && webusbErrors.length > 0) {
        toast.warning(`Drawer pulsed on ${fired}/${total}. ${webusbErrors.join(" · ")}`, { duration: 8000 });
      } else if (webusbErrors.length > 0) {
        toast.error(webusbErrors.join(" · "), { duration: 8000 });
      } else if (res.data?.warning) {
        toast.warning(res.data.warning, { duration: 7000 });
      } else if (total > 0) {
        const firstErr = res.data.pulse_results.find(r => !r.ok)?.error || "unknown";
        toast.error(`Printer pulse failed: ${firstErr}`);
      } else {
        toast.success("Cash drawer event logged");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to open cash drawer");
    }
  };

  const handleOpenCustomerDisplay = () => {
    // Reuse the live popup if one is already open in idle mode.
    const existing = window.__cfd_popup;
    if (existing && !existing.closed) {
      try { existing.focus(); toast.success("Customer display brought to front"); return; }
      catch { /* fall through and reopen */ }
    }
    // Otherwise spawn a new one. Restore saved geometry if we have it; else
    // place to the right of the primary screen.
    let left = typeof screen !== "undefined" ? screen.width : 0;
    let top = 0, width = 900, height = 1100;
    try {
      const saved = JSON.parse(localStorage.getItem("cfd_popup_geometry") || "null");
      if (saved && Number.isFinite(saved.left) && Number.isFinite(saved.top)) {
        left = saved.left; top = saved.top;
        width = saved.width || width; height = saved.height || height;
      }
    } catch { /* ignore */ }
    const features = `width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=no,location=no,status=no,popup=yes`;
    const win = window.open("/customer-display", "_blank", features);
    if (!win) { toast.error("Popup blocked. Allow pop-ups for this site."); return; }
    window.__cfd_popup = win;
    // Hard-position in case the browser ignored left/top.
    const enforce = (n = 0) => {
      try { if (!win.closed) { win.moveTo(left, top); win.resizeTo(width, height); } }
      catch { /* cross-origin during nav */ }
      if (n < 3) setTimeout(() => enforce(n + 1), 200);
    };
    enforce();
    toast.success("Customer display opened in idle mode");
  };

  // Cash drawer is only relevant for staff who handle money.
  const canOpenDrawer = ["admin", "manager", "cashier", "server"].includes(user?.role);

  const NavItem = ({ item }) => {
    const isActive = location.pathname === item.path;
    const Icon = item.icon;
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={() => navigate(item.path)}
            className={`sidebar-item w-full ${isActive ? "active" : ""}`}
            data-testid={`nav-${item.label.toLowerCase().replace(" ", "-")}`}
          >
            <Icon size={20} weight={isActive ? "fill" : "regular"} />
            <span className="hidden lg:block text-sm">{item.label}</span>
          </button>
        </TooltipTrigger>
        <TooltipContent side="right" className="lg:hidden">{item.label}</TooltipContent>
      </Tooltip>
    );
  };

  return (
    <TooltipProvider delayDuration={0}>
      <aside className="sidebar-nav" data-testid="sidebar">
        {/* Logo */}
        <div className="p-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div
              className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 overflow-hidden ${
                logoUrl ? "bg-muted" : "bg-primary"
              }`}
            >
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt="Company logo"
                  className="w-full h-full object-contain"
                  data-testid="sidebar-company-logo"
                  onError={() => setLogoUrl("")}
                />
              ) : (
                <ForkKnife size={20} weight="fill" className="text-white" data-testid="sidebar-default-logo" />
              )}
            </div>
            <span
              className="font-heading font-medium text-lg hidden lg:block flex-1 truncate"
              data-testid="sidebar-brand-name"
            >{brandName}</span>
            <div className="ml-auto">
              <ThemeToggle inline compact />
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav
          className="flex-1 py-3 overflow-y-auto"
          ref={(el) => {
            // Preserve scroll position across route changes. Each page
            // component re-mounts <Sidebar />, which normally resets
            // scrollTop to 0 — annoying when the nav is long and the
            // user has scrolled down to reach e.g. Settings. We stash
            // the last scrollTop in sessionStorage and restore it on
            // every mount.
            if (!el) return;
            const stored = Number(sessionStorage.getItem("pos_sidebar_scroll") || 0);
            if (stored && el.scrollTop !== stored) el.scrollTop = stored;
            // Attach the scroll listener once per element instance.
            if (el.dataset.scrollListenerAttached) return;
            el.dataset.scrollListenerAttached = "1";
            el.addEventListener("scroll", () => {
              sessionStorage.setItem("pos_sidebar_scroll", String(el.scrollTop));
            }, { passive: true });
          }}
        >
          <div className="space-y-0.5">
            {filteredMain.map((item) => (
              <NavItem key={item.path} item={item} />
            ))}
            {canOpenDrawer && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={handleOpenCashDrawer}
                    className="sidebar-item w-full"
                    data-testid="open-cash-drawer-btn"
                  >
                    <Money size={20} />
                    <span className="hidden lg:block text-sm">Open Cash Drawer</span>
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" className="lg:hidden">Open Cash Drawer</TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handleOpenCustomerDisplay}
                  className="sidebar-item w-full"
                  data-testid="open-customer-display-btn"
                >
                  <Monitor size={20} />
                  <span className="hidden lg:block text-sm">Customer Display</span>
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" className="lg:hidden">Customer Display</TooltipContent>
            </Tooltip>
          </div>

          {/* Management Section */}
          {showMgmt && (
            <div className="mt-3 pt-3 border-t border-border">
              <button
                onClick={() => setMgmtOpen(!mgmtOpen)}
                className={`sidebar-item w-full ${isMgmtActive ? "text-primary" : ""}`}
                data-testid="nav-management-toggle"
              >
                <Briefcase size={20} weight={isMgmtActive ? "fill" : "regular"} />
                <span className="hidden lg:flex items-center gap-1 flex-1 text-sm">
                  Management
                  {mgmtOpen ? <CaretDown size={12} className="ml-auto" /> : <CaretRight size={12} className="ml-auto" />}
                </span>
              </button>

              {mgmtOpen && (
                <div className="ml-2 lg:ml-4 mt-0.5 space-y-0.5" data-testid="management-submenu">
                  {filteredMgmt.map((item) => (
                    <NavItem key={item.path} item={item} />
                  ))}
                </div>
              )}
            </div>
          )}
        </nav>

        {/* User Info & Logout */}
        <div className="p-3 border-t border-border space-y-2">
          <div className="flex items-center gap-3 px-1">
            <div className="w-9 h-9 bg-primary/10 rounded-full flex items-center justify-center">
              <User size={18} className="text-primary" weight="bold" />
            </div>
            <div className="hidden lg:block flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
            </div>
          </div>
          
          <Button
            variant="outline"
            onClick={handleLogout}
            className="w-full justify-center lg:justify-start gap-2 text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive"
            data-testid="logout-btn"
          >
            <SignOut size={18} weight="bold" />
            <span className="hidden lg:block">Sign Out</span>
          </Button>
        </div>
      </aside>
    </TooltipProvider>
  );
};

export default Sidebar;
